import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const EditPopup = ({ data, onClose, onSubmit }) => {
  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    familyname: Yup.string().required("Father / Husband Name is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
      .required("Mobile is required"),
    village: Yup.string().required("Village is required"),
    city: Yup.string().required("City is required"),
    assettype: Yup.string().required("Asset Type is required"),
    amount: Yup.number()
      .nullable()
      .when("assetType", {
        is: (value) => value === "cash" || value === "online",
        then: (schema) =>
          schema
            .required(
              "Amount is required when Asset Type is Cash or Online payment"
            )
            .positive("Amount must be positive"),
        otherwise: (schema) => schema.notRequired(),
      }),
    thingname: Yup.string()
      .nullable()
      .when("assetType", {
        is: (value) => value === "things" || value === "ornaments",
        then: (schema) =>
          schema.required("Please enter the thing you received"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 className="text-xl font-bold mb-4 text-center">Edit Details</h2>
        <Formik
          initialValues={data}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSubmit(values);
            onClose(); // Close the popup after submitting
          }}
        >
          {({ errors, touched, values }) => (
            <Form>
              {/* Name */}
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <Field
                  id="name"
                  name="name"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.name && touched.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Family Name */}
              <div className="mb-4">
                <label
                  htmlFor="familyname"
                  className="block text-sm font-medium text-gray-700"
                >
                  Father / Husband Name
                </label>
                <Field
                  id="familyname"
                  name="familyname"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.familyname && touched.familyname && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.familyname}
                  </p>
                )}
              </div>

              {/* Mobile */}
              <div className="mb-4">
                <label
                  htmlFor="mobile"
                  className="block text-sm font-medium text-gray-700"
                >
                  Mobile
                </label>
                <Field
                  id="mobile"
                  name="mobile"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.mobile && touched.mobile && (
                  <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
                )}
              </div>

              {/* Village */}
              <div className="mb-4">
                <label
                  htmlFor="village"
                  className="block text-sm font-medium text-gray-700"
                >
                  Village
                </label>
                <Field
                  id="village"
                  name="village"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.village && touched.village && (
                  <p className="text-red-500 text-xs mt-1">{errors.village}</p>
                )}
              </div>

              {/* City */}
              <div className="mb-4">
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-gray-700"
                >
                  City
                </label>
                <Field
                  id="city"
                  name="city"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
                {errors.city && touched.city && (
                  <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                )}
              </div>

              {/* Asset Type */}
              <div className="mb-4">
                <label
                  htmlFor="assettype"
                  className="block text-sm font-medium text-gray-700"
                >
                  Asset Type
                </label>
                <Field
                  id="assettype"
                  name="assettype"
                  as="select"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Asset Type</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="ornaments">Ornaments</option>
                  <option value="things">Things</option>
                </Field>
                {errors.assettype && touched.assettype && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.assettype}
                  </p>
                )}
              </div>

              {/* Conditional Field: Amount */}
              {values.assettype === "cash" || values.assettype === "online" ? (
                <div className="mb-4">
                  <label
                    htmlFor="amount"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Amount
                  </label>
                  <Field
                    id="amount"
                    name="amount"
                    type="number"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.amount && touched.amount && (
                    <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
                  )}
                </div>
              ) : null}

              {/* Conditional Field: Thing Name */}
              {values.assettype === "things" ||
              values.assettype === "ornaments" ? (
                <div className="mb-4">
                  <label
                    htmlFor="thingname"
                    className="block text-sm font-medium text-gray-700"
                  >
                    What I received
                  </label>
                  <Field
                    id="thingname"
                    name="thingname"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.thingname && touched.thingname && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.thingname}
                    </p>
                  )}
                </div>
              ) : null}

              {/* Actions */}
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EditPopup;
